import { useEffect, useRef, useState } from 'react';
import { Button, notification } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAuthStore } from '@stores/auth.store';

const CHECK_EVERY_MS = 2 * 60 * 1000;

function readCurrentEntryScript() {
    if (typeof document === 'undefined') return '';
    const scripts = Array.from(document.querySelectorAll('script[src]'));
    const entry = scripts.find((el) => {
        const src = String(el.getAttribute('src') || '');
        return src.includes('/assets/') && src.endsWith('.js');
    });
    return entry?.getAttribute('src') || '';
}

function readEntryFromHtml(html) {
    if (!html) return '';
    const match = html.match(/<script[^>]+src="([^"]*\/assets\/[^"]+\.js)"/i);
    return match?.[1] || '';
}

async function hasNewBuild() {
    const currentEntry = readCurrentEntryScript();
    if (!currentEntry) return false;

    const res = await fetch('/', {
        cache: 'no-store',
        headers: { Accept: 'text/html' },
    });
    const html = await res.text();
    const latestEntry = readEntryFromHtml(html);
    if (!latestEntry) return false;
    return latestEntry !== currentEntry;
}

const AppUpdateWatcher = () => {
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
    const [api, contextHolder] = notification.useNotification();
    const [hasNotified, setHasNotified] = useState(false);
    const checkingRef = useRef(false);

    useEffect(() => {
        if (!isAuthenticated || import.meta.env.DEV) return undefined;

        let active = true;
        let timerId = null;

        const checkAndNotify = async () => {
            if (!active || checkingRef.current || hasNotified) return;
            checkingRef.current = true;
            try {
                const updateAvailable = await hasNewBuild();
                if (!active || !updateAvailable) return;
                api.info({
                    message: 'New update available',
                    description: 'A newer version is ready. Refresh now to get the latest changes.',
                    duration: 0,
                    btn: (
                        <Button
                            type="primary"
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => window.location.reload()}
                        >
                            Refresh now
                        </Button>
                    ),
                });
                setHasNotified(true);
            } catch (error) {
                console.error(error);
            } finally {
                checkingRef.current = false;
            }
        };

        void checkAndNotify();
        timerId = window.setInterval(() => {
            void checkAndNotify();
        }, CHECK_EVERY_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                void checkAndNotify();
            }
        };
        window.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            active = false;
            if (timerId) window.clearInterval(timerId);
            window.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [api, hasNotified, isAuthenticated]);

    return contextHolder;
};

export default AppUpdateWatcher;
